import { Link } from "@tanstack/react-router";
import { CONSULTING_PAGES } from "@/components/ConsultingExpertise";
import { ctaClick } from "@/lib/ctaTracking";
import { SITE, NAV, SOLUTIONS, SOCIAL } from "@/lib/site";
import { SocialIcons } from "./SocialIcons";
import { MarkdownMirrorLink } from "./MarkdownMirrorLink";
import blueprint from "@/assets/blueprint.jpg";

export function SiteFooter() {
  return (
    <footer className="relative border-t border-border bg-surface overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src={blueprint}
          alt=""
          aria-hidden
          width={1920}
          height={1024}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover opacity-[0.12]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-surface/85 via-surface/90 to-surface" />
      </div>
      <div className="relative container-page py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Link to="/" className="text-xl font-semibold tracking-tight">
              {SITE.name}<span className="text-primary">.</span>
            </Link>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-ink-soft">
              {SITE.tagline}. Advisory on electronic security, smart automation,
              luxury home cinema, auditorium AV and intelligent building technology.
            </p>
            <div className="mt-6">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground mb-3">Follow</h3>
              <SocialIcons size="sm" />
              <a href={SOCIAL.linkedin} target="_blank" rel="noopener me" className="tap-target mt-3 inline-flex text-sm font-medium text-primary hover:text-foreground">
                Arif Khan on LinkedIn →
              </a>
            </div>

          </div>

          <div className="lg:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground mb-4">Explore</h3>
            <ul className="space-y-2.5 text-sm">
              {NAV.slice(1, 7).map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="tap-target inline-flex py-1 text-ink-soft hover:text-primary transition-colors">
                    {n.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/for-architects" className="tap-target inline-flex py-1 text-ink-soft hover:text-primary transition-colors">
                  For Architects
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground mb-4">Solutions</h3>
            <ul className="space-y-2.5 text-sm">
              {SOLUTIONS.map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/solutions/$slug"
                    params={{ slug: s.slug }}
                    className="tap-target inline-flex py-1 text-ink-soft hover:text-primary transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
            <h3 className="mt-8 text-xs font-semibold uppercase tracking-widest text-foreground mb-4">Consulting Expertise</h3>
            <ul className="space-y-2.5 text-sm">
              {CONSULTING_PAGES.map((p) => (
                <li key={p.to}>
                  <Link to={p.to} className="tap-target inline-flex py-1 text-ink-soft hover:text-primary transition-colors">
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-foreground mb-4">Contact</h3>
            <ul className="space-y-2.5 text-sm text-ink-soft">
              <li><a href={`mailto:${SITE.email}`} onClick={ctaClick("email", "footer")} className="tap-target inline-flex py-1 hover:text-primary">{SITE.email}</a></li>
              {!/X{4,}/.test(SITE.phoneE164) && (
                <li>
                  <a href={`tel:${SITE.phoneE164}`} onClick={ctaClick("phone", "footer")} className="tap-target inline-flex py-1 hover:text-primary">{SITE.phone}</a>
                </li>
              )}
              <li className="leading-relaxed">
                {SITE.address.line1}, {SITE.address.line2}, {SITE.address.city} – {SITE.address.postalCode}
              </li>
              <li>{SITE.location}</li>
              <li>{SITE.role}</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-ink-soft">
          <div>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <MarkdownMirrorLink className="hover:text-primary transition-colors" />
            <span className="font-mono uppercase tracking-widest">Security · Automation · Luxury AV</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
